import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Grid, IconButton, Typography } from "@mui/material";
import ProductCommissionPopup from "./ProductCommissionPopup";
import CustomTextField from "../../common/CustomTextField";
import { CreateSearchAgency, ProductCommissionAgency, saveAgency, Option, updateAgencyInfoObject, editAgency, addAgencyProductCommission, fetchProductCommissionsByAgencyId, useAgencyReducer, editAgencyProductCommission, fetchAgencyById, clearAgencyObject, clearAgencyProductCommissionList, fetchAssociateAgencyList, AgencyOption, fetchIsV3AgencyExist,  getPracticeListByUserRoleInAgency, fetchActiveAgencyExecutives } from "../../../redux/slice/agency/AgencySlice";
import { useForm } from "react-hook-form";
import Button from "../../common/Button";
import PageBackButton from "../../common/PageBackButton";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { AddCircleOutlineOutlined, BusinessOutlined, CancelOutlined, ModeOutlined, PaymentsOutlined, SaveOutlined } from "@mui/icons-material";
import Table from "../../common/CustomTable";
import { useLocation, useNavigate } from "react-router-dom";
import { MainErrorInfo, SingleAlertInfo, setMainErrorObj, setSingleAlertObj } from "../../../redux/slice/commonSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { ProductInfo, fetchProductsByUserRoleData, useProductListReducer } from "../../../redux/slice/product/productListSlice";
import { fullName, isIsrCommissionType, isOverrideCommissionType, isV3CommissionType, sortNames } from "../../../utils/Utility";
import CustomPatternTextField from "../../common/CustomPatternTextField";
import moment from "moment";
import { OptionPopupObjectProps } from "../../common/OptionsPopup";
import { CodeTable, getCodeTableValuesByTableName, useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import CustomIconButton from "../../common/CustomIconButton";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { selectIsValueFoundInToken } from "../../../redux/slice/authSlice";
import { COMMISSION_STATUS, COMMISSION_TYPE_CODETABLE } from "../../../constants/applicationConstants";
import { useUserReducer } from "../../../redux/slice/userSlice";
import { getPracticesByUserRole, PracticeInfo, usePracticeSlice } from "../../../redux/slice/practiceSlice";
import { useNavigation } from "../../../hooks/useNavigation";
import { SectionHeader } from "../../ui/ContentSection";
import { useModalStyles } from "../../common/useModalStyles";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
}

export interface Row {
  productCommissionId: number | undefined;
  productId: number[] | undefined;
  productName: string | undefined;
  associateType: string | undefined;
  associateAgencyId: number | undefined;
  associatePracticeId: number | undefined;
  associateAccountRepId: number | undefined;
  commissionRate: number | string | undefined,
  commissionPayTerms: string | undefined;
  associateAgencyName: string | undefined;
  associatePracticeName: string | undefined;
  associateAccountRepName: string | undefined;
  associateAgencyCommission: string | number | undefined;
  effectiveDate: string | undefined;
  inactiveDate: string | undefined;
  rowIndex: number | undefined;
  associateEntityName?: string | undefined;
  overrideAgency?: string | undefined;
}

export const isDateEarlier = (dateStr: string, targetDateStr: string): boolean => {
  const date = new Date(dateStr);
  const targetDate = new Date(targetDateStr);
  return date <= targetDate;
}

export const isDateBetween = (startDateStr: string, endDateStr: string, targetDateStr: string): boolean => {
  const startDate = new Date(startDateStr);
  const endDate = new Date(endDateStr);
  const targetDate = new Date(targetDateStr);

  return targetDate >= startDate && targetDate <= endDate;
};

export const isSameRecord = (selectedCommision: Row, commission: ProductCommissionAgency): boolean => {
  let effectiveDate = commission.effectiveDate ? moment(commission.effectiveDate?.toString())?.format("MM/DD/YYYY") : "";
  let inactiveDate = commission.inactiveDate ? moment(commission.inactiveDate?.toString())?.format("MM/DD/YYYY") : "";
  if (selectedCommision.productId == commission.productId && selectedCommision.commissionRate == commission.commissionRate && selectedCommision.effectiveDate == effectiveDate
    && selectedCommision.inactiveDate == inactiveDate && selectedCommision.associateAgencyId == commission.associateAgencyId && selectedCommision.productCommissionId == commission.productCommissionId) {
    return true;
  }
  return false;
}

export const hasProductIdOverlap = (productIds1: number[] | undefined | number, productIds2: number[] | undefined | number): boolean => {
  if (!productIds1 || !productIds2) {
    return false;
  }

  const array1 = Array.isArray(productIds1) ? productIds1 : [productIds1];
  const array2 = Array.isArray(productIds2) ? productIds2 : [productIds2];

  return array1.some(id1 => array2.includes(id1));
};

const AgencyForm: React.FC = () => {
  const location = useLocation();
  const agencyData = location.state?.agency;
  const isEditing = Boolean(agencyData);
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const {
    tokens: mTokens,
    isDark: isDarkModal,
    inputFieldSx,
    dropdownWrapSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();
  const cardSx = {
    borderRadius: "16px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: mTokens.bg,
    backgroundImage: "none",
    overflow: "hidden" as const,
    boxShadow: isDarkModal ? "0 10px 20px rgba(0,0,0,0.1)" : "none",
  };
  const tablePanelSx = {
    border: `1px solid ${mTokens.border}`,
    borderRadius: "12px",
    overflow: "hidden" as const,
    backgroundColor: isDarkModal ? "rgba(15,23,42,0.35)" : "rgba(248,250,252,0.85)",
    p: 1,
  };
  const outlineAccentBtnSx = {
    textTransform: "none" as const,
    fontWeight: 700,
    fontSize: "0.82rem",
    borderRadius: "10px",
    height: 36,
    px: 1.6,
    color: mTokens.accent,
    borderColor: mTokens.accent,
    backgroundColor: "transparent",
    whiteSpace: "nowrap" as const,
    letterSpacing: "0.04em",
    "&:hover": {
      backgroundColor: `${mTokens.accent}14`,
      borderColor: mTokens.accent,
      boxShadow: "none",
    },
    "& .MuiTypography-root": { color: `${mTokens.accent} !important` },
    "& .MuiButton-startIcon, & .MuiButton-endIcon": { color: mTokens.accent },
  };
  const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: mTokens.textSecondary, mb: 0.7, display: "flex", alignItems: "center", gap: 0.4, flexWrap: "wrap" }}>
      {label}{required && <Box component="span" sx={{ color: mTokens.accent }}>*</Box>}
    </Typography>
  );
  const [isCommTypeOverride, setIsCommTypeOverride] = useState(false);
  const [open, setOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [editCommission, setEditCommission] = useState(false);
  const [selectedAssociateType, setSelectedAssociateType] = useState<any>('');
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setError,
    formState: { errors },
  } = useForm<CreateSearchAgency>();
  const [totalPages, setTotalPages] = useState(0);

  const selectedCommissionType = watch("commissionType");
  const PRACTICE_ENTITY: string = "PRACTICE";
  const AGENCY_ENTITY: string = "AGENCY";
  const ACCOUNT_REPRESENTATIVE_ENTITY: string = "ACCOUNTREP";
  const { agencyProductCommissionList, agency, associateAgencyList, isV3AgencyExists,practiceByUserRoleAgencySlice,agencyExecutives,editStatus } = useAgencyReducer();
  const [commissionList, setCommissionList] = useState<ProductCommissionAgency[]>([]);
  const defaultValues = useCallback(() => {
    if (agency) {
      reset(agency);
    } else {
      reset();
    }
  }, [agency, reset]);

  //Mount
  useEffect(() => {
    dispatch(fetchIsV3AgencyExist());
    dispatch(fetchProductsByUserRoleData(false));
    dispatch(fetchActiveAgencyExecutives({ getAgencyAdmins: true, isInactive: true }));
    dispatch(fetchAssociateAgencyList());
  }, [dispatch]);

  useEffect(() => {
    defaultValues();
  }, [defaultValues]);

  const canAccessCommissionActionsAdd = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessCommissionActions", COMMISSION_STATUS.CREATE)(state));
  const canAccessCommissionActionsEdit = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessCommissionActions", COMMISSION_STATUS.EDIT)(state));

  const columns: Column[] = [
    { id: "productName", label: "Product", minWidth: 100 },
    { id: "effectiveDate", label: "Effective Date", minWidth: 100 },
    { id: "inactiveDate", label: "Inactive Date", minWidth: 100 },
    { id: "commissionRate", label: "Service Fee", minWidth: 100, align: "left" },
    ...(canAccessCommissionActionsEdit ? [{
      id: "action",
      label: "Action",
      minWidth: 100,
      align: "left" as const,
    }] : []),
  ];

  const { productsByUserRole } = useProductListReducer();
  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } = useCodetablesSlice();

  const commTypeOptions = useMemo(() => {
    if (!allCodeTablesValuesMap) return [];

    const commissionTypes = getCodeTableValuesByTableName(
      allCodeTablesValuesMap,
      COMMISSION_TYPE_CODETABLE
    );

    if (!Array.isArray(commissionTypes)) return [];

    return commissionTypes
      .filter((item) => {
        const keepV3 =
          !isV3CommissionType(allCodeTablesValuesMap, item.keyName) ||
          !isV3AgencyExists ||
          isV3CommissionType(allCodeTablesValuesMap, selectedCommissionType);
        const hideIsr =
          isIsrCommissionType(allCodeTablesValuesMap, item.keyName) &&
          !isIsrCommissionType(allCodeTablesValuesMap, selectedCommissionType);
        return keepV3 && !hideIsr;
      })
      .map((item) => ({
        label: item.displayValue,
        value: item.keyName,
      }));
  }, [allCodeTablesValuesMap, isV3AgencyExists, selectedCommissionType]);

  useEffect(() => {
    if (agencyData?.agencyId) {
      dispatch(fetchAgencyById(agencyData.agencyId));
      const param = {
        agencyId: agencyData.agencyId,
        itemsPerPage: 10,
        currentPage: currentPage
      }
      dispatch(fetchProductCommissionsByAgencyId(param));
    }
  }, [dispatch, agencyData]);

  useEffect(()=>{
    if(editStatus==="success" && agencyData?.agencyId){
      dispatch(fetchAgencyById(agencyData.agencyId));
      const param = {
        agencyId: agencyData.agencyId,
        itemsPerPage: 10,
        currentPage: currentPage
      }
     setSelectedAssociateType('')
      dispatch(fetchProductCommissionsByAgencyId(param));
    }
  },[editStatus,dispatch])

  useEffect(() => {
    if (agencyProductCommissionList && productsByUserRole) {
      setCommissionList(agencyProductCommissionList?.content);
    }
  }, [agencyProductCommissionList, productsByUserRole]);

  useEffect(() => {
    return () => {
      dispatch(clearAgencyObject());
      dispatch(clearAgencyProductCommissionList());
    };
  }, [dispatch]);

  useEffect(() => {
    if (selectedCommissionType === "Override") {
      setIsCommTypeOverride(true);
    } else {
      setIsCommTypeOverride(false);
    }
  }, [selectedCommissionType]);

  const [selectedCommission, setSelectedCommission] = useState<Row>({
    productCommissionId: 0,
    productId: [],
    productName: undefined,
    associateType: undefined,
    associateAgencyId: undefined,
    associatePracticeId: undefined,
    associateAccountRepId: undefined,
    commissionRate: undefined,
    commissionPayTerms: undefined,
    associateAgencyName: undefined,
    associatePracticeName: undefined,
    associateAccountRepName: undefined,
    associateAgencyCommission: undefined,
    effectiveDate: undefined,
    inactiveDate: undefined,
    rowIndex: 1
  });
  const [optionPopupShow, setOptionPopupShow] = useState<OptionPopupObjectProps>({ message: "", show: false });

  const handleClickOpen = () => {
    const initialCommission: Row = {
      productCommissionId: 0,
      productId: [],
      productName: undefined,
      associateType: undefined,
      associateAgencyId: undefined,
      associatePracticeId: undefined,
      associateAccountRepId: undefined,
      commissionRate: undefined,
      commissionPayTerms: undefined,
      associateAgencyName: undefined,
      associatePracticeName: undefined,
      associateAccountRepName: undefined,
      associateAgencyCommission: undefined,
      effectiveDate: undefined,
      inactiveDate: undefined,
      rowIndex: 1
    }
    setSelectedCommission(initialCommission);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditCommission(false);
    setSelectedAssociateType('')
  };

  const productOptions = useMemo(() => {
    let options: Option[] = [];
    if (productsByUserRole) {
      const sortedProductList: ProductInfo[] = sortNames(productsByUserRole, 'productName');
      sortedProductList?.map((option) => {
        options.push({ label: option.manufacturerName + " - " + option.productName, value: option.productId })
      });
    };
    return options;
  }, [productsByUserRole]);

  const associateAgencyOptions = useMemo(() => {
    let options: AgencyOption[] = [];
    if (associateAgencyList) {
      const sortedAgencyList: CreateSearchAgency[] = sortNames(associateAgencyList, 'agencyName');
      sortedAgencyList?.forEach((option) => {
        if (option.agencyName !== undefined && option.agencyId !== undefined) {
          if (isEditing && isCommTypeOverride && option.agencyId === agencyData.agencyId) {
            return; // This acts like 'continue' by skipping to the next iteration
          }
          options.push({ label: option.agencyName, value: option.agencyId });
        }
      });
    };
    return options;
  }, [associateAgencyList, isCommTypeOverride]);
  useEffect(()=>{
    dispatch(getPracticeListByUserRoleInAgency(true));
  },[dispatch])
  //CS-429
  const AssociateOptions = [PRACTICE_ENTITY,ACCOUNT_REPRESENTATIVE_ENTITY,AGENCY_ENTITY]

  const associateOptions = useMemo<Option[]>(() => {
    let options: Option[] = [];
    if (AssociateOptions && AssociateOptions.length > 0) {
      options = AssociateOptions.map((option) => ({
        value: option,
        label: option,
      }));
    }
    return options;
  }, [AssociateOptions]);
  const practiceListOptions = useMemo(() => {
    let options: Option[] = [];
    if (practiceByUserRoleAgencySlice && practiceByUserRoleAgencySlice.length > 0) {
      const sortedPracticeInfo: PracticeInfo[] = sortNames(practiceByUserRoleAgencySlice, 'practiceName');
      sortedPracticeInfo?.map(practice => {
        options.push({ value: practice.practiceId, label: practice.practiceName });
      });
    }
    return options;
  }, [practiceByUserRoleAgencySlice]);

  const agencyExecutivesOptions = useMemo(() => {
    let options: any[] = [];
    agencyExecutives?.map((executive) => {
        options.push({
          value: executive.userId,
          label:
            // executive.agencyName +
            // " - " +
            fullName(
              null,
              executive.firstName,
              executive.middleName,
              executive.lastName
            ),
        });
      
    });

    return options;
  }, [ agencyExecutives])

  //

  const [stateList, setStateList] = React.useState<CodeTable[]>([]);

  const stateListOptions = useMemo(() => {
    return stateList.map(item => ({
      label: item.displayValue,
      value: item.keyName
    }));
  }, [stateList]);

  const hasDateOverlap = (
    newStart: string,
    newEnd: string,
    existingStart: string,
    existingEnd: string
  ): boolean => {
    const newStartDate = new Date(newStart);
    const newEndDate = newEnd ? new Date(newEnd) : null;
    const existingStartDate = new Date(existingStart);
    const existingEndDate = existingEnd ? new Date(existingEnd) : null;

    if (!newEndDate && !existingEndDate) {
      return true;
    }
    if (!newEndDate) {
      return !existingEndDate || newStartDate <= existingEndDate;
    }
    if (!existingEndDate) {
      return existingStartDate <= newEndDate;
    }
    return newStartDate <= existingEndDate && existingStartDate <= newEndDate;
  };

  useEffect(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      const sortedStateList = [
        ...getCodeTableValuesByTableName(allCodeTablesValuesMap, "StateList"),
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
      setStateList(sortedStateList);
    }
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

  const submitCommission = (commission: ProductCommissionAgency) => {

    let updatedCommissionList: ProductCommissionAgency[] = [];

    let notValidCommission = false;
    let errorMsg = "";

    let newEffectiveDate = commission.effectiveDate ? moment(commission.effectiveDate?.toString())?.format("MM/DD/YYYY") : "";
    let newInativeDate = commission.inactiveDate ? moment(commission.inactiveDate?.toString())?.format("MM/DD/YYYY") : "";

    if (newInativeDate === "Invalid date") {
      notValidCommission = true;
      errorMsg = "Inactive Date is Invalid";
    }

    if (newEffectiveDate === "Invalid date") {
      notValidCommission = true;
      errorMsg = "Effective Date is Invalid";
    }

    if (newInativeDate !== "" && newInativeDate !== "Invalid date" && newEffectiveDate !== "Invalid date" && new Date(newEffectiveDate) > new Date(newInativeDate)) {
      notValidCommission = true;
      errorMsg = "Effective Date should be less than Inactive Date";
    }

    if (!notValidCommission) {
      const productIds = Array.isArray(commission.productId) ? commission.productId : [commission.productId];

      for (const singleProductId of productIds) {
        for (let i = 0; i < commissionList.length; i++) {
          const existingCommission: ProductCommissionAgency = commissionList[i];

          if (!hasProductIdOverlap(existingCommission.productId, singleProductId)) {
            continue;
          }

          if (!!selectedCommission.productId && isSameRecord(selectedCommission, existingCommission)) {
            continue;
          }

          if (commission.associateAgencyId && existingCommission.associateAgencyId !== commission.associateAgencyId) {
            continue;
          }

          if(commission.associatePracticeId && existingCommission.associatePracticeId != commission.associatePracticeId){
            continue;
          }

          const existingEffectiveDate = existingCommission.effectiveDate ? moment(existingCommission.effectiveDate?.toString())?.format("MM/DD/YYYY") : "";
          const existingInactiveDate = existingCommission.inactiveDate ? moment(existingCommission.inactiveDate?.toString())?.format("MM/DD/YYYY") : "";

          if (hasDateOverlap(newEffectiveDate, newInativeDate, existingEffectiveDate, existingInactiveDate)) {
            notValidCommission = true;
            errorMsg = "Valid existing service fee exists in the system";
            break;
          }
        }

        if (notValidCommission) {
          break;
        }
      }
    }

    if (notValidCommission) {
      let errorObj: MainErrorInfo = {
        title: "",
      };

      errorObj.title = editCommission ? "Error updating agency product service fee" : "Error creating agency product service fee";
      errorObj.errorMessageStream = errorMsg;
      setOpen(false);
      dispatch(setMainErrorObj(errorObj));
      return;
    }

    const productIds = Array.isArray(commission.productId) ? commission.productId : [commission.productId];

    if (!isEditing) {
      if (!editCommission) {
        // Create new commission entries for each product ID
        for (const singleProductId of productIds) {
          const newCommission: ProductCommissionAgency = {
            ...commission,
            productId: singleProductId
          };
          updatedCommissionList.push(newCommission);
        }
        setCommissionList([...commissionList, ...updatedCommissionList]);
      } else {
        // Update existing commission
        let changedCommission: ProductCommissionAgency = commissionList[commission.rowIndex];

        if (Array.isArray(commission.productId) && !Array.isArray(changedCommission.productId)) {
          const filteredCommissions = commissionList.filter((_, index) => index !== commission.rowIndex);

          for (const singleProductId of commission.productId) {
            filteredCommissions.push({
              ...changedCommission,
              productId: singleProductId,
              commissionRate: commission.commissionRate,
              commissionPayTerms: commission.commissionPayTerms,
              associateAgencyId: commission.associateAgencyId,
              effectiveDate: commission.effectiveDate,
              inactiveDate: commission.inactiveDate
            });
          }

          setCommissionList(filteredCommissions);
        } else {
          changedCommission.productId = commission.productId;
          changedCommission.commissionRate = commission.commissionRate;
          changedCommission.commissionPayTerms = commission.commissionPayTerms;
          changedCommission.associateAgencyId = commission.associateAgencyId;
          changedCommission.effectiveDate = commission.effectiveDate;
          changedCommission.inactiveDate = commission.inactiveDate;
          setCommissionList([...commissionList]);
        }
      }
    } else {
      if (Array.isArray(commission.productId)) {
        // Create separate commission entries for each product ID
        for (const singleProductId of productIds) {
          const singleCommission: ProductCommissionAgency = {
            ...commission,
            productId: singleProductId,
            agencyId: agencyData.agencyId,
            inactiveDate: commission.inactiveDate ? moment(commission.inactiveDate?.toString())?.format("MM/DD/YYYY") : "",
            effectiveDate: commission.effectiveDate ? moment(commission.effectiveDate?.toString())?.format("MM/DD/YYYY") : "",
            commissionRate: commission.commissionRate ? Number(commission.commissionRate) : 0
          };

          if (!isCommTypeOverride) {
            delete singleCommission.associateAgencyId;
          }

          handleSaveAgencyProductCommission(singleCommission);
        }
      } else {
        commission.agencyId = agencyData.agencyId;
        commission.inactiveDate = commission.inactiveDate ? moment(commission.inactiveDate?.toString())?.format("MM/DD/YYYY") : "";
        commission.effectiveDate = commission.effectiveDate ? moment(commission.effectiveDate?.toString())?.format("MM/DD/YYYY") : "";
        commission.commissionRate = commission.commissionRate ? Number(commission.commissionRate) : 0;
        if (!isCommTypeOverride) {
          delete commission.associateAgencyId;
        }
        handleSaveAgencyProductCommission(commission);
      }
    }
    setOpen(false);
    setEditCommission(false);
    setSelectedAssociateType('');
  };

  const handleSaveAgencyProductCommission = async (data: ProductCommissionAgency) => {
    let errorObj: MainErrorInfo = {
      title: "",
    };

    try {
      let response;
      if (!editCommission) {
        response = await dispatch(addAgencyProductCommission(data));
      } else {
        response = await dispatch(editAgencyProductCommission(data));
      }
      if (response.payload && response.payload.message === 'SUCCESS') {

        let alertMessage = editCommission ? "Agency Product Service Fee Updated Successfully!" : "Agency Product Service Fee Created Successfully!";

        const alertObj: SingleAlertInfo = {
          message: alertMessage,
          alertType: "success",
        };
        if (!editCommission) {
          if (isEditing) {
            const param = {
              agencyId: agencyData.agencyId,
              itemsPerPage: 10,
              currentPage: currentPage
            }
            dispatch(fetchProductCommissionsByAgencyId(param));
          } else {
            setCommissionList([...commissionList, data]);
          }

        } else {

          const updatedCommissionList: ProductCommissionAgency[] = [...commissionList];
          let changedCommission: ProductCommissionAgency = {
            ...updatedCommissionList[data.rowIndex],
            productId: data.productId,
            // productName: data.productName,
            commissionRate: data.commissionRate,
            commissionPayTerms: data.commissionPayTerms,
            associateAgencyId: data.associateAgencyId,
            associatePracticeId: data.associatePracticeId,
            associateAccountRepId: data.associateAccountRepId,
            effectiveDate: data.effectiveDate,
            inactiveDate: data.inactiveDate,
          }

          updatedCommissionList[data.rowIndex] = changedCommission;
          setCommissionList(updatedCommissionList);
        }

        dispatch(setSingleAlertObj(alertObj));
      } else {
        let errorSentence = '';
        if (response.payload.errors.length <= 0) {
          errorSentence = response.payload.message || "An unknown error occurred.";
        } else {
          response.payload.errors.forEach((error: any, index: any) => {
            if (error.field) {
              errorSentence += `${error.field}: ${error.errorMessage}\n`;
            } else {
              errorSentence += `${error.reason}\n`;
            }
          });
        }

        errorObj.title = editCommission ? "Agency Product Service Fee Update Failed!" : "Agency Product Service Fee Creation Failed!";
        errorObj.errorMessageStream = errorSentence;
        dispatch(setMainErrorObj(errorObj));
      }
    } catch (error) {
      console.log("Error creating agency product commission :", error);
      errorObj.title = editCommission ? "Error updating agency product service fee" : "Error creating agency product service fee";
      errorObj.errorMessageStream = "" + error;
      dispatch(setMainErrorObj(errorObj));
    }
  };

  const findLabelByValue = (productId: number | undefined) => {
    const option = productOptions?.find(option => Number(option.value) == productId)
    return option ? option.label : "";
  }


  const findProductCommission = (commission: ProductCommissionAgency) => {
    if (associateAgencyList && associateAgencyList.length > 0) {
      const associateAgency: CreateSearchAgency | undefined = associateAgencyList?.find((associateAgency: CreateSearchAgency) => commission.associateAgencyId == associateAgency.agencyId);
      if (associateAgency && associateAgency.agencyProductCommisions) {
        const associateProdComm: ProductCommissionAgency | undefined = associateAgency.agencyProductCommisions?.find((prodComm: ProductCommissionAgency) => prodComm.productId === commission.productId);
        if (associateProdComm && associateProdComm.commissionRate) {
          return associateProdComm.commissionRate;
        }
      }
    }
    return "0%";
  }

  const getProductName = (productId: number | undefined, fallbackName: string | undefined): string => {
    if (!productId) {
      return fallbackName || "";
    }

    const foundName = findLabelByValue(productId);
    return foundName !== "" ? foundName : (fallbackName || "");
  };

  const commissionRow: Row[] = useMemo(() => {
    let commission: Row[] = [];
    if (commissionList) {
      let rowIndex = 0;

      commissionList.forEach((commissionItem: ProductCommissionAgency) => {
        if (Array.isArray(commissionItem.productId) && commissionItem.productId.length > 0) {
          commissionItem.productId.forEach((singleProductId) => {
            commission.push({
              productCommissionId: commissionItem.productCommissionId,
              productId: [singleProductId],
              productName: getProductName(singleProductId, commissionItem.productName),
              associateType: commissionItem.associateType,
              associateAgencyId: commissionItem.associateAgencyId,
              associatePracticeId: commissionItem.associatePracticeId,
              associateAccountRepId: commissionItem.associateAccountRepId,
              commissionRate: commissionItem.commissionRate + " %",
              commissionPayTerms: commissionItem.commissionPayTerms,
              associateAgencyName: commissionItem.associateAgencyId ? associateAgencyList?.find((agency: CreateSearchAgency) => agency.agencyId == commissionItem.associateAgencyId)?.agencyName : "",
              associatePracticeName: commissionItem.associatePracticeId ? practiceByUserRoleAgencySlice?.find((practice: any) => practice.practiceId === commissionItem.associatePracticeId)?.practiceName : "",
              associateAccountRepName: commissionItem.associateAccountRepId
                ? (() => {
                  const executive = agencyExecutives?.find((executive: any) => executive.userId == commissionItem.associateAccountRepId);
                  return executive ? `${executive.firstName} ${executive.lastName} ${executive.middleName}` : "";
                })()
                : "",
              associateAgencyCommission: commissionItem.associateAgencyId ? findProductCommission(commissionItem) : "",
              effectiveDate: commissionItem.effectiveDate ? moment(commissionItem.effectiveDate?.toString())?.format("MM/DD/YYYY") : "",
              inactiveDate: commissionItem.inactiveDate ? moment(commissionItem.inactiveDate?.toString())?.format("MM/DD/YYYY") : "",
              rowIndex: rowIndex++,
            });
          });
        }
        // Handle case for single product ID (number or undefined)
        else {
          commission.push({
            productCommissionId: commissionItem.productCommissionId,
            productId: commissionItem.productId,
            productName: getProductName(
              Array.isArray(commissionItem.productId) ? commissionItem.productId[0] : commissionItem.productId,
              commissionItem.productName
            ),
            associateType: commissionItem.associateType,
            associateAgencyId: commissionItem.associateAgencyId,
            associatePracticeId: commissionItem.associatePracticeId,
            associateAccountRepId: commissionItem.associateAccountRepId,
            commissionRate: commissionItem.commissionRate + " %",
            commissionPayTerms: commissionItem.commissionPayTerms,
            associateAgencyName: commissionItem.associateAgencyId ? associateAgencyList?.find((agency: CreateSearchAgency) => agency.agencyId == commissionItem.associateAgencyId)?.agencyName : "",
            associatePracticeName: commissionItem.associatePracticeId ? practiceByUserRoleAgencySlice?.find((practice: any) => practice.practiceId === commissionItem.associatePracticeId)?.practiceName : "",
            associateAccountRepName: commissionItem.associateAccountRepId
              ? (() => {
                const executive = agencyExecutives?.find((executive: any) => executive.userId == commissionItem.associateAccountRepId);
                return executive ? `${executive.firstName} ${executive.lastName} ${executive.middleName}` : "";
              })()
              : "",
            associateAgencyCommission: commissionItem.associateAgencyId ? findProductCommission(commissionItem) : "",
            effectiveDate: commissionItem.effectiveDate ? moment(commissionItem.effectiveDate?.toString())?.format("MM/DD/YYYY") : "",
            inactiveDate: commissionItem.inactiveDate ? moment(commissionItem.inactiveDate?.toString())?.format("MM/DD/YYYY") : "",
            rowIndex: rowIndex++,
          });
        }
      });
    }
    return commission;
  }, [commissionList, agencyProductCommissionList, associateAgencyList]);

  useEffect(() => {
    handleTotalPages();
  }, [commissionRow]);

  const onSubmit = async (data: CreateSearchAgency) => {
    data = finalizeSavingAgency(data);
    if (!isEditing && isCommTypeOverride && data.agencyProductCommisions) {
      for (let i = 0; i < data.agencyProductCommisions?.length; i++) {
        const existingCommission: ProductCommissionAgency = data.agencyProductCommisions[i];
        if (!existingCommission.associateAgencyId && !existingCommission.associatePracticeId && !existingCommission.associateAccountRepId) {
          let errorObj: MainErrorInfo = {
            title: "",
          };

          errorObj.title = "Error creating agency";
          errorObj.errorMessageStream = "Each product service fee in the override agency should have an associate agency";
          dispatch(setMainErrorObj(errorObj));
          return;
        }
      }
    }
    if (isEditing) {
      data = { ...data, agencyId: agencyData.agencyId };
      dispatch(updateAgencyInfoObject(data));
    }
    handleSaveAgency(data);
  };



  const finalizeSavingAgency = (agency: CreateSearchAgency) => {

    const updatedCommissionList = commissionList.map((commission) => {
      const updatedCommission = { ...commission };
      updatedCommission.agencyId = isEditing ? agencyData?.agencyId : 0;
      updatedCommission.inactiveDate = updatedCommission.inactiveDate ? moment(updatedCommission.inactiveDate?.toString())?.format("MM/DD/YYYY") : "";
      updatedCommission.effectiveDate = updatedCommission.effectiveDate ? moment(updatedCommission.effectiveDate?.toString())?.format("MM/DD/YYYY") : "";
      delete updatedCommission.rowIndex;
      if (!isOverrideCommissionType(allCodeTablesValuesMap, agency.commissionType)) {
        delete updatedCommission.associateAgencyId;
      }
      return updatedCommission;
    });


    if (!isEditing) {
      agency.agencyProductCommisions = updatedCommissionList;
    }

    return agency;
  }

  const onClose = () => {
    navigate(-1);
    reset();
    if (location.state || isEditing) {
      location.state.agency = null;
    }
    setSelectedAssociateType('')

  }

  const handleSaveAgency = async (data: CreateSearchAgency) => {
    let errorObj: MainErrorInfo = {
      title: "",
    };

    try {
      let response;
      if (isEditing) {
        response = await dispatch(editAgency(data));        
      } else {
        response = await dispatch(saveAgency(data));
      }

      if (response.payload && response.payload.message === 'SUCCESS' || response.payload && response.payload.message === "Agency updated successfully") {
        navigate(-1);

        let alertMessage = isEditing ?
          "Agency Updated Successfully!" :
          "Agency Created Successfully!";

        const alertObj: SingleAlertInfo = {
          message: alertMessage,
          alertType: "success",
        };

        dispatch(setSingleAlertObj(alertObj));
      } else {
        let errorSentence = '';
        if (response.payload.errors.length <= 0) {
          errorSentence = response.payload.message || "An unknown error occurred.";
        } else {
          response.payload.errors.forEach((error: any, index: any) => {
            if (error.field) {
              errorSentence += `${error.field}: ${error.errorMessage}\n`;
              if (error.field === 'email') {
                setError('email', {
                  type: 'manual',
                  message: error.errorMessage,
                });
              }
            } else {
              errorSentence += `${error.reason}\n`;
            }
          });
        }

        errorObj.title = isEditing ? "Agency Update Failed!" : "Agency Creation Failed!";
        errorObj.errorMessageStream = errorSentence;
        dispatch(setMainErrorObj(errorObj));
      }
    } catch (error) {
      console.log(isEditing ? "Error updating agency:" : "Error creating agency:", error);
      errorObj.title = isEditing ? "Error updating agency" : "Error creating agency";
      errorObj.errorMessageStream = "" + error;
      dispatch(setMainErrorObj(errorObj));
    }
  };

  const pagination = (pageNumber: number) => {
    if (isEditing) {
      const param = {
        agencyId: agencyData.agencyId,
        itemsPerPage: 10,
        currentPage: pageNumber + 1
      }
      dispatch(fetchProductCommissionsByAgencyId(param));
    }
    setCurrentPage(pageNumber);
  };

  const handleTotalPages = () => {
    setTotalPages(getTotalPages());
  }

  const getTotalPages = () => {
    if (isEditing) {
      if (agencyProductCommissionList) {
        return agencyProductCommissionList.totalPages;
      }
      return 1;
    } else {
      if (commissionRow.length > 10) {
        if (commissionRow.length % 10 === 0) {
          return commissionRow.length % 10;
        } else {
          return (commissionRow.length - commissionRow.length % 10) / 10 + 1;
        }
      }
      return 1;
    }

  }

  const handleActionEdit = (row: Row, rowIndex: number) => {
    setEditCommission(true);
    setSelectedAssociateType(row.associateAgencyId ? "AGENCY" : row.associateType)
    // setSelectedAssociateType(row.associateType)

    const selectedCommission: Row = {
      productCommissionId: row.productCommissionId,
      productId: row.productId === undefined ? undefined : Array.isArray(row.productId) ? row.productId : [row.productId],
      productName: row.productName,
      associateType:row.associateAgencyId? "AGENCY" :row.associateType,
      associateAgencyId: row.associateAgencyId,
      associatePracticeId: row.associatePracticeId,
      associateAccountRepId: row.associateAccountRepId,
      commissionRate: row.commissionRate ? Number(row.commissionRate.toString().split(" ")[0]) : "",
      commissionPayTerms: row.commissionPayTerms,
      associateAgencyName: row.associateAgencyName,
      associatePracticeName: row.associatePracticeName,
      associateAccountRepName: row.associateAccountRepName,
      associateAgencyCommission: row.associateAgencyCommission,
      effectiveDate: row.effectiveDate,
      inactiveDate: row.inactiveDate,
      rowIndex: row.rowIndex
    }
    setSelectedCommission(selectedCommission);
    setOpen(true);
  }

  const actionComponent = (row: Row, rowIndex: number): JSX.Element => {
    return <>
      <CustomIconButton
        color="info"
        size="small"
        variant="outlined"
        onClick={() => handleActionEdit(row, rowIndex)}
        tooltipMessage="Edit"
        tooltipPlacement='top'>
        <ModeOutlined fontSize='small' />
      </CustomIconButton>
    </>
  }

  const confirmOptionPopup = () => {
    handleSubmit(onSubmit)()
    setOptionPopupShow({ message: "", show: false });
  }

  const cancelOptionPopup = () => {
    setOptionPopupShow({ message: "", show: false });
  }

  const updateAgencyValues = () => {
    {
      setOptionPopupShow({
        message: "Do You Really want to update agency details? ",
        show: true,
        variant: "confirm",
        buttons: [{ name: "Confirm", color: 'primary', onClick: confirmOptionPopup }, { name: "Cancel", color: 'secondary', onClick: cancelOptionPopup }]
      })
    }
  }

  const addAssociateAgencyColumns = (columns: Column[]): Column[] => {
    const modifiedColumns = [...columns];
    if (isCommTypeOverride) {
      modifiedColumns.splice(columns.length - 1, 0, {
        id: "associateAgencyCommission", label: "Associate Agency Svc Fee", minWidth: 100, align: "center",
        format: (value: number) => { return value + " %" }
      });
      modifiedColumns.splice(columns.length - 1, 0, { id: "associatePracticeName", label: "Associate Practice", minWidth: 100 });
      modifiedColumns.splice(columns.length - 1, 0, { id: "associateAccountRepName", label: "Associate AccountRep", minWidth: 100 });
      modifiedColumns.splice(columns.length - 1, 0, { id: "associateAgencyName", label: "Associate Agency", minWidth: 100 });
    } else {
      if (columns.length == 7) {
        modifiedColumns.splice(columns.length - 1, 1);
        modifiedColumns.splice(columns.length - 1, 1);
      }
    }
    return modifiedColumns;
  }

  return (
    <Box sx={{ mx: { xs: "10px", sm: "20px" }, mt: "12px", pb: "72px" }}>
      <Box mb={2} display="flex" alignItems="flex-start" gap={1.5}>
        <PageBackButton tooltip={null} onClick={() => onClose()} />
        <Box>
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.12em", mb: 0.3 }}>
            Agencies / Form
          </Typography>
          <Typography component="h1" sx={{ fontSize: "1.6rem", fontWeight: 800, color: mTokens.textPrimary, lineHeight: 1.1 }}>
            Agency Management
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary, mt: 0.4 }}>
            {isEditing ? "Edit agency details." : "Create a new agency."}
          </Typography>
        </Box>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={cardSx} data-testid="mock-card">
            <SectionHeader icon={<BusinessOutlined fontSize="small" />} title="Agency Info" />
            <Box sx={{ px: { xs: 2, md: 2.8 }, py: 2 }}>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} sm={6}>
                  <FieldLabel label="Agency Name" required />
                  <Box sx={inputFieldSx(!!errors.agencyName)}>
                    <CustomTextField
                      control={control}
                      controllerName="agencyName"
                      label=""
                      size="small"
                      rules={{ required: "Agency Name is required." }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FieldLabel label="Svc Fee Type" required />
                  <Box sx={dropdownWrapSx(!!errors.commissionType)}>
                    <CustomDropdownField
                      name="commissionType"
                      control={control}
                      options={commTypeOptions}
                      label=""
                      size="small"
                      rules={{ required: "Service fee type is required." }}
                      disabled={isEditing}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FieldLabel label="Email" required />
                  <Box sx={inputFieldSx(!!errors.email)}>
                    <CustomTextField
                      control={control}
                      controllerName="email"
                      label=""
                      size="small"
                      rules={{
                        required: "Email is required.",
                        pattern: {
                          value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i,
                          message: "Invalid email.",
                        },
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FieldLabel label="Phone" required />
                  <Box sx={inputFieldSx(!!errors.phone)}>
                    <CustomPatternTextField
                      control={control}
                      name="phone"
                      label=""
                      size="small"
                      rules={{ required: "Phone is required." }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FieldLabel label="Street Address" required />
                  <Box sx={inputFieldSx(!!errors.streetAddress)}>
                    <CustomTextField
                      control={control}
                      controllerName="streetAddress"
                      label=""
                      size="small"
                      rules={{ required: "Street Address is required." }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FieldLabel label="Unit #" />
                  <Box sx={inputFieldSx(!!errors.unitNumber)}>
                    <CustomTextField
                      control={control}
                      controllerName="unitNumber"
                      label=""
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FieldLabel label="ZipCode" required />
                  <Box sx={inputFieldSx(!!errors.zipcode)}>
                    <CustomTextField
                      control={control}
                      controllerName="zipcode"
                      label=""
                      size="small"
                      rules={{ required: "ZipCode is required." }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FieldLabel label="City" required />
                  <Box sx={inputFieldSx(!!errors.city)}>
                    <CustomTextField
                      control={control}
                      controllerName="city"
                      label=""
                      size="small"
                      rules={{ required: "City is required." }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FieldLabel label="State" required />
                  <Box sx={dropdownWrapSx(!!errors.state)}>
                    <CustomDropdownField
                      name="state"
                      control={control}
                      options={stateListOptions}
                      label=""
                      size="small"
                      rules={{ required: "State is required." }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box sx={cardSx}>
            <SectionHeader
              icon={<PaymentsOutlined fontSize="small" />}
              title="Product Service Fees"
              action={
                canAccessCommissionActionsAdd ? (
                  <Button
                    onClick={handleClickOpen}
                    color="info"
                    variant="outlined"
                    removeUpperCase
                    startIcon={<AddCircleOutlineOutlined />}
                    sx={outlineAccentBtnSx}
                  >
                    Add Product Service Fee
                  </Button>
                ) : undefined
              }
            />
            <Box sx={{ px: { xs: 1.5, md: 2.8 }, py: 2 }}>
              <Box sx={{ ...tablePanelSx, overflowX: "auto" }}>
                <Table
                  columns={addAssociateAgencyColumns(columns)}
                  rows={commissionRow || []}
                  actionComponent={(row: any, rowIndex: number) =>
                    actionComponent(row, rowIndex)
                  }
                  emptyText="No Orders Found"
                  totalPages={totalPages}
                  pageNumber={(thisPage: number) => pagination(thisPage)}
                />
              </Box>
            </Box>
          </Box>

          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            gap={1.2}
            justifyContent="flex-end"
          >
            {isEditing ? (
              <Button
                type="submit"
                onClick={updateAgencyValues}
                removeUpperCase
                startIcon={<SaveOutlined />}
                fullWidth
                sx={{
                  ...primaryBtnSx,
                  minWidth: { sm: 160 },
                  width: { xs: "100%", sm: "auto" },
                  backgroundColor: mTokens.accent,
                  boxShadow: "0 4px 18px rgba(20,184,166,.35)",
                  color: "#fff",
                  "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
                  "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "#fff" },
                }}
              >
                Update Agency
              </Button>
            ) : (
              <Button
                type="submit"
                removeUpperCase
                startIcon={<SaveOutlined />}
                fullWidth
                sx={{
                  ...primaryBtnSx,
                  minWidth: { sm: 160 },
                  width: { xs: "100%", sm: "auto" },
                  backgroundColor: mTokens.accent,
                  boxShadow: "0 4px 18px rgba(20,184,166,.35)",
                  color: "#fff",
                  "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
                  "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "#fff" },
                }}
              >
                Save Agency
              </Button>
            )}
            <Button
              color="error"
              variant="outlined"
              removeUpperCase
              startIcon={<CancelOutlined />}
              onClick={() => onClose()}
              fullWidth
              sx={{
                ...cancelBtnSx,
                minWidth: { sm: 120 },
                width: { xs: "100%", sm: "auto" },
                "& .MuiTypography-root": { color: "inherit", fontWeight: 700 },
                "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "inherit" },
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </form>

      <ProductCommissionPopup
        open={open}
        selectedAssociateTypeOption={selectedAssociateType}
        setSelectedAssociateType={setSelectedAssociateType}
        productOptions={productOptions}
        associateAgencyOptions={associateAgencyOptions}
        associateOptions={associateOptions}
        agencyExecutivesOptions={agencyExecutivesOptions}
        practiceListOptions={practiceListOptions}
        isCommTypeOverride={isCommTypeOverride}
        editCommission={editCommission}
        defaultValue={selectedCommission}
        handleClose={handleClose}
        submitCommission={submitCommission}
      />
    </Box>
  );
};

export default AgencyForm;
